// Helpers

import { isNull, merge } from "lodash";
import { local_schema, ILocalState$, IRemoteState$, ICreateStateProps, IObservers, IActions } from "./types";
import { api, IAPI, IContentExtract, IMeasure } from "@/core/shared/api";
import { transforms } from "@/core/shared/transforms";
import { z } from "zod";
import { legend } from "@/core/utils/legend-state/core";
import { rc } from "../../rc";
import { auth } from "@/core/state/auth";
import { metadata } from "@/core/state/metadata";
import { report_builder } from "@/features/report-builder/state";
import { ITransformedContentExtract } from "../../../extracts/instance/state/types";
import { IPeersetCompany } from "@/features/report-builder/state/types";
import dayjs from "dayjs";

// Config
const config: z.infer<typeof local_schema>["config"] = {
  company: {
    measure_filter_to_measure_name_map: {
      EPS: {
        default: ["PROMOTER_RATIO_PER_COMPANY", "PASSIVE_RATIO_PER_COMPANY", "DETRACTOR_RATIO_PER_COMPANY"],
      },
      EPS_COUNT: {
        default: ["PROMOTER_COUNT_PER_COMPANY", "PASSIVE_COUNT_PER_COMPANY", "DETRACTOR_COUNT_PER_COMPANY"],
      },
    },
    filters: {
      hide: false,
    },
  },
  peer_set_avg: {
    PEER_SET_AVG_MEAN: {
      id: "PEER_SET_AVG_MEAN",
      label: "Mean",
    },
    PEER_SET_AVG_MEDIAN: {
      id: "PEER_SET_AVG_MEDIAN",
      label: "Median",
    },
  },
  measure: {
    hide: false,
    default: "EPS",
    measures: [
      {
        id: "EPS",
        label: "Net Rating Composition",
        unit: "%",
        acronym: "EPS",
        showSign: true,
        domain: [0, 100],
        filter: {
          checked: true,
          disabled: false,
        },
      },
      {
        id: "EPS_COUNT",
        label: "Net Rating Composition: Counts",
        domain: undefined,
        compactYAxisValues: true,
        filter: {
          checked: true,
          disabled: false,
        },
      },
    ],
  },
  benchmark: {
    hide: false,
    benchmarks: [
      {
        id: "PEER_SET_AVG",
        label: "Peer set average",
        measure_filter_to_measure_name_map: {
          EPS: {
            default: ["PROMOTER_RATIO", "DETRACTOR_RATIO", "PASSIVE_RATIO"],
          },
          EPS_COUNT: {
            default: ["PROMOTER_COUNT", "DETRACTOR_COUNT", "PASSIVE_COUNT"],
          },
        },
        filter: {
          checked: true,
          disabled: false,
        },
      },
    ],
  },
  visualisation: [
    {
      id: "bar",
      label: "Bar",
    },
    {
      id: "table",
      label: "Table",
    },
  ],
};

export function createState(
  instance_id: string,
  props: ICreateStateProps,
): { local: ILocalState$; remote: IRemoteState$ } {
  const local = helpers.validateAndMergeState(
    {
      label: "Employee Positivity",
      ui_merged: false,
      config,
      views: {
        currentView: "analysis",
        analysis: {
          chart_config: {
            entities: {},
            polar_axis: {
              data_key: "group",
              label: "Company",
              domain: config.measure.measures.find((m) => m.id === "EPS")?.domain,
            },
          },
          selectors: {
            measure: {
              selectedMeasureId: "EPS",
            },
            visualisation: {
              selected: "bar",
            },
            peer_set_avg_type: {
              selected: config.peer_set_avg.PEER_SET_AVG_MEAN.id,
              applied: config.peer_set_avg.PEER_SET_AVG_MEAN.id,
            },
            peer_set_avg_mode: {
              selected: true,
              applied: true,
            },
          },
          filters: {
            company: legend.features.filter.createObservableState(),
            peer_set_avg_contributors: legend.features.filter.createObservableState(),
            timeframe: legend.features.filter.createObservableState(),
            benchmark: legend.features.filter.createObservableState(),
            applied_filters: {
              company_id: [],
              timeframe: [],
              benchmarks: [],
              peer_set_avg_contributors: [],
            },
          },
        },
      },
    },
    props.local,
  );

  const remote: IRemoteState$ = {
    measures: {
      pending: false,
      error: false,
      data: legend.sync.synced({
        initial: [],
        get: async () => {
          const instance = rc.registry.actions.getInstance(instance_id);
          try {
            instance.remote.measures.pending.set(true);
            const selected_measure_id = instance.local.views.analysis.selectors.measure.selectedMeasureId.get();

            // convert benchmarks to measure names
            const benchmark_measure_names: string[] = [];
            instance.local.views.analysis.filters.applied_filters.benchmarks.get()?.forEach((benchmark_id) => {
              const benchmark = instance.local.config.benchmark.benchmarks.get().find((b) => b.id === benchmark_id);
              if (!benchmark) return;
              const benchmark_measure_name = helpers.mapMeasureNameToParams(
                benchmark.measure_filter_to_measure_name_map,
                selected_measure_id,
              );
              benchmark_measure_names.push(...benchmark_measure_name);
            });
            const peerset = report_builder.remote.peerset.data.get();

            const benchmark_config = instance.local.config.benchmark.benchmarks.get();
            const company_config = instance.local.config.company.get();

            const applied_company_id = instance.local.views.analysis.filters.applied_filters.company_id.get();
            const applied_timeframe = instance.local.views.analysis.filters.applied_filters.timeframe.get();
            const applied_peer_set_avg_contributors =
              instance.local.views.analysis.filters.applied_filters.peer_set_avg_contributors.get();

            const applied_peer_set_avg_type = instance.local.views.analysis.selectors.peer_set_avg_type.applied.get();

            const company_measure_names = helpers.mapMeasureNameToParams(
              instance.local.config.company.measure_filter_to_measure_name_map.get(),
              selected_measure_id,
            );

            const params: Parameters<IAPI["deltabase_data_api"]["measures"]["get"]>["0"] = {
              company_id: applied_company_id,
              year: applied_timeframe,
              measure_name: company_measure_names,
              sort_field: "year",
              sort_direction: "ASC",
            };

            const peer_set_avg_params: Parameters<IAPI["deltabase_data_api"]["measures"]["get"]>["0"] = {
              company_id: applied_peer_set_avg_contributors,
              year: applied_timeframe,
              measure_name: Array.from(new Set([...benchmark_measure_names])),
              sort_field: "year",
              sort_direction: "ASC",
              use_median: applied_peer_set_avg_type === "PEER_SET_AVG_MEDIAN",
            };

            const eps_params: Parameters<IAPI["deltabase_data_api"]["measures"]["get"]>["0"] = {
              company_id: applied_company_id,
              year: applied_timeframe,
              measure_name: ["EPS_PER_COMPANY"],
              sort_field: "year",
              sort_direction: "ASC",
            };

            // Guard against no company selected - Data API will return an error 400 otherwise
            if (params.company_id?.length === 0 || peer_set_avg_params.company_id?.length === 0) {
              return [];
            }

            const promises = [
              api.deltabase_data_api.measures.get(params),
              api.deltabase_data_api.measures.get(eps_params),
            ];
            if (benchmark_measure_names.length > 0) {
              promises.push(api.deltabase_data_api.measures.get(peer_set_avg_params));
            }

            const [measures_response, eps_measure_data, peer_set_avg_response] = await Promise.all(promises);
            // ... existing code ...
            const company = Object.keys(peerset);
            for (const companyId of company) {
              // handle per company data
              const applied_company = applied_company_id?.find((c) => c === companyId);

              for (const measure_name of company_measure_names) {
                const found_measure = measures_response.data.find(
                  (m) => m.company_id === companyId && m.measure_name === measure_name,
                );

                if (!found_measure && applied_company) {
                  measures_response.data.push({
                    measure_signal: "NO_SIGNAL",
                    measure_id: "",
                    measure_name,
                    measure_range: "",
                    measure_unit: "",
                    measure_value: "",
                    brief_id: "",
                    city: "",
                    company_id: companyId,
                    continent: "",
                    cost: NaN,
                    country: "",
                    date: "",
                    function: "",
                    job_id: "",
                    leaf_level: false,
                    month: "",
                    rating: "",
                    sector: "",
                    seniority: "",
                    skill: "",
                    source_id: "",
                    source_url: "",
                    state: "",
                    topic_name: "",
                    user_name: "",
                    year: NaN,
                    years_in_current_company: "",
                  });
                }
              }

              // handle eps per company data.
              const found_eps_measure = eps_measure_data.data.find((m) => m.company_id === companyId);

              if (!found_eps_measure && applied_company) {
                eps_measure_data.data.push({
                  measure_signal: "NO_SIGNAL",
                  measure_id: "",
                  measure_name: "EPS_PER_COMPANY",
                  measure_range: "",
                  measure_unit: "",
                  measure_value: "",
                  brief_id: "",
                  city: "",
                  company_id: companyId,
                  continent: "",
                  cost: NaN,
                  country: "",
                  date: "",
                  function: "",
                  job_id: "",
                  leaf_level: false,
                  month: "",
                  rating: "",
                  sector: "",
                  seniority: "",
                  skill: "",
                  source_id: "",
                  source_url: "",
                  state: "",
                  topic_name: "",
                  user_name: "",
                  year: NaN,
                  years_in_current_company: "",
                });
              }
            }
            // ... existing code ...

            const eps_data = eps_measure_data.data
              .filter((m) => {
                return m.measure_name === "EPS_PER_COMPANY";
              })
              .map((m) => {
                const score = m.measure_signal === "NO_SIGNAL" ? null : helpers.transformMeasureScore(m, "EPS");
                return {
                  ...m,
                  measure_value: score,
                };
              })
              .sort((a, b) => {
                if (a.measure_value === null) return 1;
                if (b.measure_value === null) return -1;
                return b.measure_value - a.measure_value;
              })
              .map((m) => {
                return {
                  ...m,
                  measure_value: isNull(m.measure_value)
                    ? "No Signal"
                    : m.measure_value > 0
                      ? `+${m.measure_value}%`
                      : m.measure_value < 0
                        ? `-${m.measure_value}%`
                        : `${m.measure_value}%`,
                };
              });

            const peer_set_avg_data = peer_set_avg_response?.data ?? [];
            const data = [...measures_response?.data, ...peer_set_avg_data];

            // group by topic
            const chart_data = helpers.formatChartData({
              data,
              selected_measure_id,
              benchmark_config,
              company_config,
            });

            const table_data = helpers.formatTableData({
              data,
              selected_measure_id,
              benchmark_config,
              company_config,
              peerset,
            });

            legend.state.batch(() => {
              instance.remote.measures.pending.set(false);
              instance.remote.measures.error.set(false);
            });

            return { chart_data, table_data, eps_data };
          } catch (error) {
            legend.state.batch(() => {
              instance.remote.measures.pending.set(false);
              instance.remote.measures.error.set(true);
            });
          }
        },
        waitFor: () => {
          const token = auth.state$.token.get();
          return !!token;
        },
      }),
    },
  };

  return { local, remote };
}

export const actions: IActions = {
  views: {
    analysis: {
      selectors: {
        measure: {
          set: (instance_id: string, measure_id: string) => {
            const instance = rc.registry.actions.getInstance(instance_id);
            const measure = instance.local.config.measure.measures.get().find((m) => m.id === measure_id);
            if (!measure) return;
            legend.state.batch(() => {
              instance.local.views.analysis.selectors.measure.selectedMeasureId.set(measure_id);
              instance.local.views.analysis.chart_config.polar_axis.assign({
                label: `${measure.label}  ${measure.unit ? `(${measure.unit})` : ""}`,
                domain: measure.domain,
                compactValues: measure.compactYAxisValues,
                unit: measure.unit,
              });
            });
          },
        },
        visualisation: {
          set: (instance_id: string, visualisation: string) => {
            const instance = rc.registry.actions.getInstance(instance_id);
            instance.local.views.analysis.selectors.visualisation.selected.set(visualisation);
          },
        },
        peer_set_avg_type: {
          set: (instance_id: string, peer_set_avg_type: string) => {
            const instance = rc.registry.actions.getInstance(instance_id);
            instance.local.views.analysis.selectors.peer_set_avg_type.selected.set(peer_set_avg_type);
          },
          apply: (instance_id: string) => {
            const instance = rc.registry.actions.getInstance(instance_id);
            const selected = instance.local.views.analysis.selectors.peer_set_avg_type.selected.get();
            instance.local.views.analysis.selectors.peer_set_avg_type.applied.set(selected);
          },
        },
        peer_set_avg_mode: {
          set: (instance_id: string, peer_set_avg_mode: boolean) => {
            const instance = rc.registry.actions.getInstance(instance_id);
            instance.local.views.analysis.selectors.peer_set_avg_mode.selected.set(peer_set_avg_mode);
          },
          apply: (instance_id: string) => {
            const instance = rc.registry.actions.getInstance(instance_id);
            const selected = instance.local.views.analysis.selectors.peer_set_avg_type.selected.get();
            instance.local.views.analysis.selectors.peer_set_avg_type.applied.set(selected);
          },
        },
      },
      filters: {
        benchmark: {
          applyAll: (instance_id: string) => {
            const instance = rc.registry.actions.getInstance(instance_id);
            const filters = instance.local.views.analysis.filters.benchmark.data.get();
            const selected_measure = instance.local.views.analysis.selectors.measure.selectedMeasureId.get();

            const benchmarks: string[] = [];
            Object.values(filters).forEach((filter) => {
              if (filter.checked) {
                const benchmark = instance.local.config.benchmark.benchmarks.get().find((b) => b.id === filter.id);
                if (benchmark?.id === "PEER_SET_AVG") {
                  benchmarks.push(benchmark.id);
                }
              }
            });
            instance.local.views.analysis.filters.applied_filters.benchmarks.set(benchmarks);
          },
          toggle: (instance_id: string, filter_id: string) => {
            const instance = rc.registry.actions.getInstance(instance_id);
            legend.features.filter.toggle({
              state: instance.local.views.analysis.filters.benchmark,
              params: { id: filter_id },
            });
          },
          toggleGroup: (instance_id: string, filter_group: string) => {
            const instance = rc.registry.actions.getInstance(instance_id);
            legend.features.filter.toggleGroup({
              state: instance.local.views.analysis.filters.benchmark,
              params: { group: filter_group },
            });
          },
          getFiltersByGroup: (instance_id: string) => {
            const instance = rc.registry.actions.getInstance(instance_id);
            const out = legend.features.filter.getFiltersByGroup({
              state: instance.local.views.analysis.filters.benchmark,
            });
            return out;
          },
        },
        peer_set_avg_contributors: {
          applyAll: (instance_id: string) => {
            const instance = rc.registry.actions.getInstance(instance_id);
            const applied: string[] = [];
            const filters = instance.local.views.analysis.filters.peer_set_avg_contributors.data.get();
            Object.values(filters).forEach((filter) => {
              if (filter.checked) {
                applied.push(filter.id);
              }
            });
            instance.local.views.analysis.filters.applied_filters.peer_set_avg_contributors.set(applied);
          },
          toggle: (instance_id: string, filter_id: string) => {
            const instance = rc.registry.actions.getInstance(instance_id);
            legend.features.filter.toggle({
              state: instance.local.views.analysis.filters.peer_set_avg_contributors,
              params: { id: filter_id },
            });
          },
          toggleGroup: (instance_id: string, filter_group: string) => {
            const instance = rc.registry.actions.getInstance(instance_id);
            legend.features.filter.toggleGroup({
              state: instance.local.views.analysis.filters.peer_set_avg_contributors,
              params: { group: filter_group },
            });
          },
          getFiltersByGroup: (instance_id: string) => {
            const instance = rc.registry.actions.getInstance(instance_id);
            const out = legend.features.filter.getFiltersByGroup({
              state: instance.local.views.analysis.filters.peer_set_avg_contributors,
            });
            return out;
          },
        },
        company: {
          applyAll: (instance_id: string) => {
            const instance = rc.registry.actions.getInstance(instance_id);
            const applied: string[] = [];
            const filters = instance.local.views.analysis.filters.company.data.get();
            Object.values(filters).forEach((filter) => {
              if (filter.checked) {
                applied.push(filter.id);
              }
            });
            instance.local.views.analysis.filters.applied_filters.company_id.set(applied);
          },
          toggle: (instance_id: string, filter_id: string) => {
            const instance = rc.registry.actions.getInstance(instance_id);
            legend.features.filter.toggle({
              state: instance.local.views.analysis.filters.company,
              params: { id: filter_id },
            });
          },
          toggleGroup: (instance_id: string, filter_group: string) => {
            const instance = rc.registry.actions.getInstance(instance_id);
            legend.features.filter.toggleGroup({
              state: instance.local.views.analysis.filters.company,
              params: { group: filter_group },
            });
          },
          getFiltersByGroup: (instance_id: string) => {
            const instance = rc.registry.actions.getInstance(instance_id);
            const out = legend.features.filter.getFiltersByGroup({
              state: instance.local.views.analysis.filters.company,
            });
            return out;
          },
        },
        timeframe: {
          applyAll: (instance_id: string) => {
            const instance = rc.registry.actions.getInstance(instance_id);
            const applied: string[] = [];
            const filters = instance.local.views.analysis.filters.timeframe.data.get();
            Object.values(filters).forEach((filter) => {
              if (filter.checked) {
                applied.push(filter.id);
              }
            });
            instance.local.views.analysis.filters.applied_filters.timeframe.set(applied);
          },
          toggle: (instance_id: string, filter_id: string) => {
            const instance = rc.registry.actions.getInstance(instance_id);
            legend.features.filter.toggle({
              state: instance.local.views.analysis.filters.timeframe,
              params: { id: filter_id },
            });
          },
          toggleGroup: (instance_id: string, filter_group: string) => {
            const instance = rc.registry.actions.getInstance(instance_id);
            legend.features.filter.toggleGroup({
              state: instance.local.views.analysis.filters.timeframe,
              params: { group: filter_group },
            });
          },
          toggleByRange: (instance_id, range) => {
            const instance = rc.registry.actions.getInstance(instance_id);
            const filters_obs = instance.local.views.analysis.filters.timeframe.data;
            const filters_arr = Object.values(filters_obs.get());
            const [startYear, endYear] = range.split("-");

            // For single year selection
            if (!endYear) {
              const targetFilter = filters_arr.find((filter) => filter.id === startYear);
              const newCheckedState = !targetFilter?.checked;

              filters_arr.forEach((filter) => {
                const checked = filter.id === startYear ? newCheckedState : false;
                filters_obs[filter.id].checked.set(checked);
              });
              return;
            }

            // For year range selection
            legend.state.batch(() => {
              // Check if all filters in range are already checked
              const allInRangeChecked = filters_arr.every((filter) => {
                const year = parseInt(filter.id);
                const inRange = year >= parseInt(startYear) && year <= parseInt(endYear);
                return !inRange || filter.checked;
              });

              // Toggle all filters in range based on current state
              // Get current checked state
              const currentCheckedFilters = filters_arr
                .filter((f) => f.checked)
                .map((f) => parseInt(f.id))
                .sort();

              // Get target range years
              const targetYears = Array.from(
                { length: parseInt(endYear) - parseInt(startYear) + 1 },
                (_, i) => parseInt(startYear) + i,
              ).sort();

              // Compare if current matches target range
              const isCurrentRange = JSON.stringify(currentCheckedFilters) === JSON.stringify(targetYears);

              filters_arr.forEach((filter) => {
                const year = parseInt(filter.id);
                const inRange = year >= parseInt(startYear) && year <= parseInt(endYear);
                // If current selection matches target range, toggle them all off
                // Otherwise set the new range
                const checked = isCurrentRange ? false : inRange;
                filters_obs[filter.id].checked.set(checked);
              });
            });
          },
          getFiltersByGroup: (instance_id: string) => {
            const instance = rc.registry.actions.getInstance(instance_id);
            const out = legend.features.filter.getFiltersByGroup({
              state: instance.local.views.analysis.filters.timeframe,
            });
            return out;
          },
        },
      },
    },
  },
};

export const observers: IObservers = {
  view: {
    analysis: {
      chart_config: {
        sync_to_state: (instance_id: string) => {
          if (!instance_id) return;
          legend.state.observe(() => {
            const instance = rc.registry.actions.getInstance(instance_id);

            const peerset = report_builder.remote.peerset.data.get();

            // sync chart config
            const selected_measure_id = instance.local.views.analysis.selectors.measure.selectedMeasureId.get();
            const applied_company_id = instance.local.views.analysis.filters.applied_filters.company_id.get() ?? [];
            const applied_benchmarks = instance.local.views.analysis.filters.applied_filters.benchmarks.get() ?? [];

            const benchmark_measure_names: string[] = [];
            instance.local.views.analysis.filters.applied_filters.benchmarks.get()?.forEach((benchmark_id) => {
              const benchmark = instance.local.config.benchmark.benchmarks.get().find((b) => b.id === benchmark_id);
              if (!benchmark) return;
              const benchmark_measure_name = helpers.mapMeasureNameToParams(
                benchmark.measure_filter_to_measure_name_map,
                selected_measure_id,
              );
              benchmark_measure_names.push(...benchmark_measure_name);
            });

            const company_measure_names = helpers.mapMeasureNameToParams(
              instance.local.config.company.measure_filter_to_measure_name_map.get(),
              selected_measure_id,
            );

            const config: z.infer<typeof local_schema>["views"]["analysis"]["chart_config"]["entities"] = {};

            // add benchmark entities to chart config
            // applied_benchmarks.forEach((benchmark_id, idx) => {
            //   const benchmark = instance.local.config.benchmark.benchmarks.get().find((b) => b.id === benchmark_id);
            //   if (!benchmark) return;

            //   if (benchmark.id === "PEER_SET_AVG") {
            //     benchmark_measure_names.forEach((measure_name) => {
            //       debugger;
            //       config[benchmark.id].push({
            //         id: measure_name,
            //         label: benchmark.label,
            //         color: `hsl(var(--chart-deltabase-peer_set_avg))`,
            //       });
            //     });
            //   }
            // });

            // add company entities to chart config
            applied_company_id.forEach((company_id) => {
              const company = peerset[company_id];
              if (!company) return;
              company_measure_names.forEach((measure_name, idx) => {
                if (!config[company_id]) {
                  config[company_id] = [];
                }
                config[company_id].push({
                  id: measure_name,
                  label: company?.company_name ?? "",
                  color: `hsl(var(--chart-deltabase-company-${idx + 1}))`,
                });
              });
            });

            instance.local.views.analysis.chart_config.entities.set(config);
          });
        },
      },
      filters: {
        benchmark: {
          init: (instance_id: string) => {
            if (!instance_id) return;
            legend.state.observe<{ initialised: boolean }>((e) => {
              if (e.previous?.initialised) return;
              const instance = rc.registry.actions.getInstance(instance_id);
              const benchmarks = instance.local.config.benchmark.benchmarks.get();

              if (!benchmarks || benchmarks.length === 0) return;

              // populate filter state
              legend.state.batch(() => {
                legend.features.filter.batchAddFilter({
                  state: instance.local.views.analysis.filters.benchmark,
                  params: {
                    data: benchmarks.map((benchmark) => {
                      const out = {
                        id: benchmark.id,
                        label: benchmark.label,
                        checked: benchmark.filter.checked,
                        disabled: benchmark.filter.disabled,
                        group: "",
                      };
                      return out;
                    }),
                  },
                });
                actions.views.analysis.filters.benchmark.applyAll(instance_id);
              });

              return { initialised: true };
            });
          },
        },
        peer_set_avg_contributors: {
          init: (instance_id: string) => {
            if (!instance_id) return;
            legend.state.observe<{ peerset_key: string }>((e) => {
              const instance = rc.registry.actions.getInstance(instance_id);
              const peerset = report_builder.remote.peerset.data.get();
              const current_peerset_key = JSON.stringify(Object.keys(peerset));

              if (!peerset || current_peerset_key.length === 0 || current_peerset_key === e.previous?.peerset_key) {
                return;
              }

              const companies = Object.values(peerset);

              // populate filter state
              legend.state.batch(() => {
                legend.features.filter.batchAddFilter({
                  state: instance.local.views.analysis.filters.peer_set_avg_contributors,
                  params: {
                    data: companies.map((company, idx) => ({
                      id: company.company_id,
                      label: company.company_name,
                      checked: true,
                      disabled: false,
                      group: "",
                    })),
                  },
                });
                actions.views.analysis.filters.peer_set_avg_contributors.applyAll(instance_id);
              });

              const peerset_key = JSON.stringify(Object.keys(peerset));

              return { peerset_key };
            });
          },
        },
        company: {
          init: (instance_id: string) => {
            if (!instance_id) return;
            legend.state.observe<{ peerset_key: string }>((e) => {
              const instance = rc.registry.actions.getInstance(instance_id);
              const peerset = report_builder.remote.peerset.data.get();
              const current_peerset_key = JSON.stringify(Object.keys(peerset));

              if (!peerset || current_peerset_key.length === 0 || current_peerset_key === e.previous?.peerset_key) {
                return;
              }

              const companies = Object.values(peerset);

              // populate filter state
              legend.state.batch(() => {
                legend.features.filter.batchAddFilter({
                  state: instance.local.views.analysis.filters.company,
                  params: {
                    data: companies.map((company, idx) => ({
                      id: company.company_id,
                      label: company.company_name,
                      checked: true,
                      disabled: false,
                      group: "",
                    })),
                  },
                });
                actions.views.analysis.filters.company.applyAll(instance_id);
              });

              const peerset_key = JSON.stringify(Object.keys(peerset));

              return { peerset_key };
            });
          },
        },
        timeframe: {
          init: (instance_id: string) => {
            if (!instance_id) return;
            legend.state.observe<{ initialised: boolean }>((e) => {
              if (e.previous?.initialised) return;
              const instance = rc.registry.actions.getInstance(instance_id);
              const timeframe_metadata = metadata.remote$.data.get().cached_transformed["year"];
              const company_search_timeframe = report_builder.local.search.time_periods.get();

              if (!timeframe_metadata || timeframe_metadata.length === 0 || company_search_timeframe.length === 0) {
                return { initialised: false };
              }

              // populate filter state
              legend.state.batch(() => {
                legend.features.filter.batchAddFilter({
                  state: instance.local.views.analysis.filters.timeframe,
                  params: {
                    data: timeframe_metadata.map((timeframe) => {
                      const out = {
                        id: timeframe.meta_data_value,
                        label: timeframe.meta_data_value.toLowerCase(),
                        checked: company_search_timeframe?.includes(timeframe.meta_data_value) ?? false,
                        disabled: false,
                        group: "",
                      };
                      return out;
                    }),
                  },
                });
                actions.views.analysis.filters.timeframe.applyAll(instance_id);
              });

              return { initialised: true };
            });
          },
        },
      },
    },
  },
};

export const helpers = {
  mapMeasureNameToLabel: (name: string, verbose = false) => {
    if (name === "PROMOTER_RATIO_PER_COMPANY" || name === "PROMOTER_COUNT_PER_COMPANY") {
      return verbose ? "Promoters (4 and 5* Employee Rating)" : "Promoters";
    }
    if (name === "DETRACTOR_RATIO_PER_COMPANY" || name === "DETRACTOR_COUNT_PER_COMPANY") {
      return verbose ? "Detractor (1 and 2* Employee Rating)" : "Detractors";
    }
    if (name === "PASSIVE_RATIO_PER_COMPANY" || name === "PASSIVE_COUNT_PER_COMPANY") {
      return verbose ? "Passive (3* Employee Rating)" : "Passive";
    }
  },
  mapMeasureNameToColor: (name: string, theme: string) => {
    if (name === "PROMOTER_RATIO_PER_COMPANY" || name === "PROMOTER_COUNT_PER_COMPANY") {
      return theme === "light" ? "#6DDBB0" : "#2F7B62";
    }
    if (name === "DETRACTOR_RATIO_PER_COMPANY" || name === "DETRACTOR_COUNT_PER_COMPANY") {
      return theme === "light" ? "#FF5555" : "#6A0000";
    }
    if (name === "PASSIVE_RATIO_PER_COMPANY" || name === "PASSIVE_COUNT_PER_COMPANY") {
      return theme === "light" ? "#FFD199" : "#1C1C1F";
    }
  },
  validateAndMergeState: (state: ILocalState$, new_state?: object) => {
    if (new_state !== undefined) {
      const isValid = local_schema.safeParse(new_state);
      if (!isValid.success) {
        throw new Error(
          `Invalid props provided to createState$. Validation errors: ${JSON.stringify(isValid.error.format(), null, 2)}`,
        );
      }
      state = merge(state, new_state);
      return state;
    }
    return state;
  },

  transformMeasureScore: (measure: IMeasure, measure_type: string) => {
    if (measure_type === "NSS") {
      const score = transforms.nss.toPercentage(measure.measure_value);
      return score;
    } else if (measure_type === "PREVALENCE") {
      const score = transforms.prevalence.toPercentage(measure.measure_value);
      return score;
    } else if (measure_type === "RATING") {
      const score = transforms.rating.toNumber(measure.measure_value);
      return score;
    } else if (measure_type.includes("COUNT")) {
      const score = transforms.count.toNumber(measure.measure_value);
      return score;
    } else if (measure_type === "EPS") {
      const score = transforms.eps.toPercentage(measure.measure_value);
      return score;
    }

    return null;
  },

  mapMeasureNameToParams(measureMap: Record<string, { default: Array<string> }>, selected_measure_id: string) {
    const measure_name = measureMap[selected_measure_id];
    return measure_name.default;
  },

  formatChartData: (props: {
    data: IMeasure[];
    selected_measure_id: string;
    benchmark_config: z.infer<typeof local_schema>["config"]["benchmark"]["benchmarks"];
    company_config: z.infer<typeof local_schema>["config"]["company"];
  }) => {
    const { data, selected_measure_id, benchmark_config, company_config } = props;
    // group
    const grouped_data: Record<string, IMeasure[]> = {};
    data.forEach((measure) => {
      const group = measure.company_id;
      if (!grouped_data[group]) {
        grouped_data[group] = [];
      }
      grouped_data[group].push(measure);
    });

    type ChartRecord = Record<string, number | string | null>;
    const chart_data: ChartRecord[] = [];

    Object.entries(grouped_data).forEach(([group, measures]) => {
      const entry: ChartRecord = { group: group };
      measures.forEach((measure) => {
        const score =
          measure.measure_signal === "NO_SIGNAL" ? null : helpers.transformMeasureScore(measure, selected_measure_id);

        // Identify which entity the measure belongs to
        const matchToBenchmark = benchmark_config.find((benchmark) => {
          const benchmark_measure_name = helpers.mapMeasureNameToParams(
            benchmark.measure_filter_to_measure_name_map,
            selected_measure_id,
          );
          return benchmark_measure_name.includes(measure.measure_name);
        });
        const matchToCompany = helpers
          .mapMeasureNameToParams(company_config.measure_filter_to_measure_name_map, selected_measure_id)
          .includes(measure.measure_name);

        // If the measure belongs to a benchmark, add it to the chart data
        if (matchToBenchmark) {
          entry[matchToBenchmark.id] = score;
          return;
        }

        // If the measure belongs to the company, add it to the chart data
        if (matchToCompany) {
          entry[measure.measure_name] = score;
          return;
        }

        // If the measure doesn't belong to a benchmark or the company, throw an error
        throw new Error(`Measure not associated with a benchmark or company entity: ${measure.measure_name}`);
      });
      chart_data.push(entry);
    });

    // Check if all values across all records are null
    const allValuesNull = chart_data.every((record) => {
      // Get all values except 'year' field
      const values = Object.entries(record)
        .filter(([key]) => key !== "topic")
        .map(([_, value]) => value);
      return values.every((value) => value === null);
    });

    if (allValuesNull) {
      return [];
    }

    // Sort by cumulative value (highest to lowest)
    const sortedChartData = chart_data.sort((a, b) => {
      const getCumulativeValue = (record: ChartRecord) => {
        return Object.entries(record)
          .filter(([key]) => key !== "group") // Exclude the group field (company ID)
          .reduce((sum, [_, value]) => {
            const numValue = typeof value === "number" ? value : 0;
            return sum + (isNaN(numValue) ? 0 : numValue);
          }, 0);
      };

      const aTotal = getCumulativeValue(a);
      const bTotal = getCumulativeValue(b);

      return bTotal - aTotal; // Sort descending (highest to lowest)
    });

    return sortedChartData;
  },
  formatTableData: (props: {
    data: IMeasure[];
    selected_measure_id: string;
    benchmark_config: z.infer<typeof local_schema>["config"]["benchmark"]["benchmarks"];
    company_config: z.infer<typeof local_schema>["config"]["company"];
    peerset: Record<string, IPeersetCompany>;
  }) => {
    const { data, selected_measure_id, benchmark_config, company_config, peerset } = props;

    // group by company
    const grouped_data: Record<
      string,
      {
        label: string;
        measures: IMeasure[];
      }
    > = {};
    data.forEach((measure) => {
      const matchToBenchmark = benchmark_config.find((benchmark) => {
        const benchmark_measure_name = helpers.mapMeasureNameToParams(
          benchmark.measure_filter_to_measure_name_map,
          selected_measure_id,
        );
        return benchmark_measure_name.includes(measure.measure_name);
      });
      const matchToCompany = helpers
        .mapMeasureNameToParams(company_config.measure_filter_to_measure_name_map, selected_measure_id)
        .includes(measure.measure_name);

      // If the measure belongs to a benchmark, add it to the chart data
      if (matchToBenchmark) {
        if (!grouped_data[matchToBenchmark.id]) {
          grouped_data[matchToBenchmark.id] = {
            label: matchToBenchmark.label,
            measures: [],
          };
        }
        grouped_data[matchToBenchmark.id].measures.push(measure);
        return;
      }

      // If the measure belongs to the company, add it to the chart data
      if (matchToCompany) {
        const company = peerset[measure.company_id];
        if (!grouped_data[measure.company_id]) {
          grouped_data[measure.company_id] = {
            label: company?.company_name,
            measures: [],
          };
        }
        grouped_data[measure.company_id].measures.push(measure);
        return;
      }
    });

    type TableRecord = Record<
      string,
      {
        id: string;
        score: number | string | null;
        signal?: string;
        label: string;
      }
    >;
    const benchmark_table_data: TableRecord[] = [];
    const company_table_data: TableRecord[] = [];

    Object.entries(grouped_data).forEach(([entity_id, { label, measures }]) => {
      const entry: TableRecord = {
        company_id: {
          id: entity_id,
          score: entity_id,
          label: label,
        },
      };
      measures.forEach((measure) => {
        const score =
          measure.measure_signal === "NO_SIGNAL" ? null : helpers.transformMeasureScore(measure, selected_measure_id);

        entry[measure.measure_name] = {
          id: measure.measure_name,
          score: score,
          label: measure.measure_name,
          signal: measure.measure_signal,
        };
      });

      // Check if this entry is for a benchmark
      const isBenchmark = benchmark_config.some((benchmark) => benchmark.id === entity_id);
      if (isBenchmark) {
        benchmark_table_data.push(entry);
      } else {
        company_table_data.push(entry);
      }
    });

    const table_data = [...benchmark_table_data, ...company_table_data];

    // Check if all values across all records are null
    const allValuesNull = table_data.every((record) => {
      // Get all values except 'year' field
      const values = Object.entries(record)
        .filter(([key]) => key !== "topic")
        .map(([_, value]) => value);
      return values.every((value) => value === null);
    });

    if (allValuesNull) {
      return [];
    }

    return table_data;
  },
};
